import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import { senders } from '../utils/email-tag';
import { useEventTag } from '../utils/use-event-tag';
import { generateTag } from '../utils/tag-generator';
import { getEventTagEmailPreviewStyle } from '../styles/event-tag-email-preview-styles';
import { chromeExtURL, edgeExtURL } from '../utils/uet-tag-helper-install-urls';

// displays the preview for the email that will be sent for custom event tag
// See NotificationPlatform repo: UPTagAndEventTagInstructionsEmail_Transform.xslt and SCTagAndEventTagInstructionsEmail_Transform.xslt
function EventTagEmailPreviewUnstyled(props) {
  const {
    action,
    classes,
    currencyCode,
    goalValue,
    goalValueType,
  } = props;
  const i18n = useContext(I18nContext);

  const {
    functionName,
    getEventTag,
    tagId,
    tagType,
  } = useEventTag({
    action,
    currencyCode,
    goalValue,
    goalValueType,
  });

  const baseTag = useMemo(() => generateTag(tagType, tagId, false), [tagType, tagId]);
  const eventTag = useMemo(() => getEventTag(), [getEventTag]);
  const eventTagForPageLoad = useMemo(() => getEventTag(true), [getEventTag]);

  const isUetTagHelperSupported = tagType === 'UET';
  const isUnifiedSmart = tagType === 'Container';

  const productName = tagType ? senders[tagType] : '';

  return (
    <div className={classNames('event-tag-email-preview', classes.emailPreview)}>
      <div className="title">{i18n.getString(_TL_('Setting up your conversion tracking'))}</div>
      <div className="description">
        <div className="EmailTemplate-text">
          {i18n.getString(
            _TL_('{{productName}} user sent you a conversion tracking tag setup instructions, so you can add the tag to your website. To add this tag to your website, follow the instructions below.'),
            { productName }
          )}
        </div>
      </div>
      <div className="substep">{i18n.getString(_TL_('Step 1'))}</div>
      <div className="EmailTemplate-instruction instruction">
        {i18n.getString(_TL_('Copy the tag and paste it in between the <head> and </head> tags of every page of your website. You only need to install the global site tag once on every page of your website, even if you are tracking multiple conversions.'))}
      </div>
      <div className="ms-TextField ms-TextField--multiline root-86">
        <div className="ms-TextField-wrapper">
          <div className="text">
            {i18n.getString(_TL_('Tracking tag'))}
          </div>
          <div className="tag base-tag">
            {baseTag}
          </div>
        </div>
      </div>
      <div className="substep">{i18n.getString(_TL_('Step 2'))}</div>
      <ul>
        <li>
          <h5>{i18n.getString(_TL_('- To track event on page load'))}</h5>
        </li>
        <span>
          {i18n.getString(_TL_('Copy the snippet below and paste it in between the <head> and </head> tags of the page(s) you\'d like to track, right after the base tag. Replace the event properties as appropriate.'))}
        </span>
        <div className="ms-TextField-wrapper event-tag-wrapper">
          <div className="tag event-tag-for-page-load">
            {eventTagForPageLoad}
          </div>
        </div>
        <li>
          <h5>{i18n.getString(_TL_('- To track event on inline action:'))}</h5>
        </li>
        <span>
          {i18n.getString(
            _TL_('Copy the snippet below and paste it in between the <head> and </head> tags of the page(s) you\'d like to track, right after the base tag. Replace the event properties as appropriate. Then, call the function "{{functionName}}" when the specific action is taken i.e. button or URL is clicked.'),
            { functionName }
          )}
        </span>
        <div className="ms-TextField-wrapper event-tag-wrapper">
          <div className="tag event-tag">
            {eventTag}
          </div>
        </div>
      </ul>

      {isUetTagHelperSupported && (
        <React.Fragment>
          <div className="substep">{i18n.getString(_TL_('Step 3'))}</div>
          <div className="tag-description">
            <div className="EmailTemplate-text">
              <div className="EmailTemplate-instruction instruction">
                <span>{i18n.getString(_TL_('Next step to test your conversion goal:'))}</span>
              </div>
              <div className="EmailTemplate-instruction instruction uet-tag-helper-instructions">
                <span>
                  <ReactTemplate
                    model={{
                      edge: text => <a href={edgeExtURL} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>,
                      chrome: text => <a href={chromeExtURL} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>,
                    }}
                    template={i18n.getString(_TL_('It can take up to 24 hours for us to verify your tags. In the meantime, you can check your tag status and test whether your conversion goals are set up correctly using the Tag Helper. You can install this browser extension on {{edge}}Microsoft Edge{{/edge}} or {{chrome}}Google Chrome{{/chrome}}.'))}
                  />
                </span>
              </div>
            </div>
            <div className="EmailTemplate-text">
              <ReactTemplate
                model={{
                  a: text => <a href="https://help.ads.microsoft.com">{text}</a>,
                  productName,
                }}
                template={i18n.getString(_TL_('Please contact the {{a}}Microsoft Advertising Support team{{/a}} if you have any questions. Thank you for using Microsoft Advertising.'))}
              />

            </div>
          </div>
        </React.Fragment>
      )}

      {!isUetTagHelperSupported && (
        <div className="tag-description">
          <div className="EmailTemplate-text">
            <ReactTemplate
              model={{
                a: () => <a href="https://help.digitalmarketing.microsoft.com/#apex/27/en/30006" target="_blank" rel="noopener noreferrer">{isUnifiedSmart ? i18n.getString(_TL_('Unified Smart Support team')) : i18n.getString(_TL_('Digital Marketing Center Support team'))}</a>,
              }}
              template={i18n.getString(_TL_('Please contact the {{a}}Platform Support Team Placeholder{{/a}} if you have any questions. Thank you for using Microsoft Advertising.'))}
            />
          </div>
        </div>
      )}

      <div className="EmailTemplate-text appreciation">
        <div>{i18n.getString(_TL_('Kind regards,'))}</div>
        <div>{i18n.getString(_TL_('The {{productName}} team'), { productName })}</div>
      </div>
    </div>
  );
}

EventTagEmailPreviewUnstyled.propTypes = {
  action: PropTypes.string,
  classes: PropTypes.shape({ emailPreview: PropTypes.string }).isRequired,
  currencyCode: PropTypes.string,
  goalValue: PropTypes.string,
  goalValueType: PropTypes.oneOf(['FixedValue', 'VariantValue', 'NoValue']).isRequired,
};

EventTagEmailPreviewUnstyled.defaultProps = {
  action: undefined,
  currencyCode: undefined,
  goalValue: undefined,
};

export const EventTagEmailPreview = withDefaultStyles(EventTagEmailPreviewUnstyled, getEventTagEmailPreviewStyle);
